import React from "react";
import { useLocation } from "react-router-dom";
import { XCircle, ShoppingBag, Home, Mail, Phone, User } from "lucide-react";

const OrderFailure = () => {
  // Use the useLocation hook to access the location state
  const location = useLocation();
  
  // Extract order details from location.state
  const orderDetails = location.state || {};
  const { orderId, total, billingInfo, products, subtotal, shippingCost, taxAmount, discount, shippingMethod, errorMessage } = orderDetails;
  
  // Assuming billingInfo contains all the customer and address details
  const customerName = billingInfo?.fullName || 'N/A';
  const email = billingInfo?.email || 'N/A';
  const phone = billingInfo?.phone || 'N/A';
  const paymentMethod = billingInfo?.paymentMethod || 'N/A';
  
  // Shipping address (using billing info if separate shipping info isn't provided)
  const shippingAddress = {
    address: billingInfo?.address || 'N/A',
    city: billingInfo?.city || '',
    state: billingInfo?.state || '',
    zip: billingInfo?.zip || ''
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-sm border border-gray-200 shadow-sm">
      <div className="text-center mb-8">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-3xl text-[#333333] mb-2" style={{ fontFamily: "serif" }}>
          Order Failed
        </h2>
        <p className="text-gray-600">
          We're sorry, there was an issue processing your order. {errorMessage || "Please try again or contact support."}
        </p>
      </div>

      <div className="mb-8 p-6 bg-gray-50 border border-gray-200 rounded-sm">
        <h3 className="text-xl text-[#333333] mb-4 flex items-center" style={{ fontFamily: "serif" }}>
          <ShoppingBag className="w-5 h-5 mr-2 text-[#D4AF37]" />
          Order Summary
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Order Details</h4>
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">Order Number:</span> #{orderId || 'N/A'}</p>
              <p><span className="font-medium">Date:</span> {new Date().toLocaleDateString()}</p>
              {subtotal && <p><span className="font-medium">Subtotal:</span> ₹{subtotal?.toFixed(2)}</p>}
              {shippingCost && <p><span className="font-medium">Shipping:</span> ₹{shippingCost?.toFixed(2)}</p>}
              {taxAmount && <p><span className="font-medium">Tax:</span> ₹{taxAmount?.toFixed(2)}</p>}
              {discount && <p><span className="font-medium">Discount:</span> -₹{discount.toFixed(2)}</p>}
              {total && <p><span className="font-medium">Total:</span> ₹{total?.toFixed(2)}</p>}
              <p><span className="font-medium">Payment Method:</span> {paymentMethod}</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Customer Details</h4>
            <div className="space-y-2 text-sm">
              <p className="flex items-center">
                <User className="w-4 h-4 mr-2 text-[#D4AF37]" />
                {customerName}
              </p>
              <p className="flex items-center">
                <Mail className="w-4 h-4 mr-2 text-[#D4AF37]" />
                {email}
              </p>
              <p className="flex items-center">
                <Phone className="w-4 h-4 mr-2 text-[#D4AF37]" />
                {phone}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      {products && products.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xl text-[#333333] mb-4 flex items-center" style={{ fontFamily: "serif" }}>
            <ShoppingBag className="w-5 h-5 mr-2 text-[#D4AF37]" />
            Ordered Items
          </h3>
          <div className="bg-gray-50 p-4 rounded-sm border border-gray-200">
            {products.map((product, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                <div className="flex-1">
                  <p className="font-medium text-sm">{product.name || product.title || 'Unknown Product'}</p>
                  <p className="text-xs text-gray-600">Quantity: {product.quantity || 1}</p>
                </div>
                <div className="text-sm font-medium">
                  ₹{((product.price || 0) * (product.quantity || 1)).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-8">
        <h3 className="text-xl text-[#333333] mb-4 flex items-center" style={{ fontFamily: "serif" }}>
          <Home className="w-5 h-5 mr-2 text-[#D4AF37]" />
          Shipping Address
        </h3>
        <div className="bg-gray-50 p-4 rounded-sm border border-gray-200 text-sm">
          <p>{shippingAddress.address}</p>
          <p>{shippingAddress.city} {shippingAddress.state}</p>
          <p>{shippingAddress.zip}</p>
          {shippingMethod && (
            <p className="mt-2 text-xs text-gray-600">
              <span className="font-medium">Shipping Method:</span> {shippingMethod}
            </p>
          )}
        </div>
      </div>

      <div className="text-center">
        <p className="text-gray-600 mb-4 text-sm">
          Please try again or contact our support team for assistance.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <a
            href="/cart"
            className="bg-[#D4AF37] text-black py-2 px-6 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200 flex items-center justify-center"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Retry Order
          </a>
          <a
            href="/contact"
            className="bg-white border border-[#D4AF37] text-[#D4AF37] py-2 px-6 rounded-sm text-sm font-medium hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
          >
            <Mail className="w-4 h-4 mr-2" />
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default OrderFailure;